import React, { useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { motion, useInView } from 'framer-motion'
import Navbar from './Navbar'
import ProductPage from './ProductPage'
import { PageSEO } from '../utils/seo'

const SITE = 'https://vmdproject.com'

/* Condizioni comuni a tutti i pacchetti: le decide il titolare, non vanno cambiate senza conferma. */
const CONDIZIONI = [
  { icon: '🧾', title: 'Prezzi IVA esclusa', desc: 'Tutte le cifre sono "a partire da" e al netto dell\'IVA (22%). Il prezzo finale arriva con un preventivo scritto, dopo aver capito cosa serve davvero.' },
  { icon: '💳', title: 'Pagamenti a tappe', desc: '30% all\'avvio, 40% a metà lavoro, 30% alla consegna. Su progetti particolari le tappe si possono concordare diversamente.' },
  { icon: '☁️', title: 'Servizi esterni a carico del cliente', desc: 'Hosting, database, Vercel, Firebase o Supabase, dominio, account sviluppatore Apple e Google Play, commissioni Stripe: gli account sono intestati a te e li paghi direttamente ai fornitori.' },
  { icon: '📍', title: 'Da remoto, con trasferte', desc: 'Lavoro da remoto con clienti in tutta Italia. Per incontri e workshop mi sposto, a Napoli e in Campania e dove serve.' },
]

const FAQ_COMUNI = [
  { q: 'I prezzi includono IVA?', a: 'No. Le cifre sono a partire da, IVA esclusa (22%). Il preventivo finale è scritto e dettaglia cosa è incluso.' },
  { q: 'Hosting, database e servizi esterni sono compresi?', a: 'No, sono a carico del cliente: hosting, database, Vercel, Firebase o Supabase, dominio, account sviluppatore Apple e Google Play, commissioni dei pagamenti. Gli account restano intestati a te, così non dipendi da me per gestirli.' },
  { q: 'Come funzionano i pagamenti?', a: '30% all\'avvio, 40% a metà lavoro, 30% alla consegna. Si possono concordare tappe diverse se il progetto lo richiede.' },
  { q: 'Lavori solo da remoto?', a: 'Lavoro da remoto e mi sposto per incontri e workshop, a Napoli, in Campania e dove serve.' },
]

function serviceJsonLd(s) {
  const url = `${SITE}/servizi/${s.slug}`
  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Service',
        '@id': `${url}#service`,
        name: s.name,
        description: s.seo.description,
        url,
        serviceType: s.name,
        provider: { '@id': `${SITE}/#org` },
        areaServed: { '@type': 'Country', name: 'Italia' },
        offers: {
          '@type': 'Offer',
          url,
          priceSpecification: {
            '@type': 'PriceSpecification',
            minPrice: s.price,
            priceCurrency: 'EUR',
            valueAddedTaxIncluded: false,
            ...(s.priceUnit && { unitText: s.priceUnit }),
          },
        },
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE}/` },
          { '@type': 'ListItem', position: 2, name: 'Servizi', item: `${SITE}/servizi` },
          { '@type': 'ListItem', position: 3, name: s.name, item: url },
        ],
      },
    ],
  }
}

/* ─── Dati dei pacchetti ─────────────────────────────────────── */
export const SERVIZI = [
  {
    slug: 'consulenza-tecnica',
    image: { height: 667, alt: "Diagramma di architettura di un'app su un pannello di vetro" },
    name: 'Consulenza e analisi del progetto',
    short: 'Hai un\'idea e non sai da dove partire: la trasformiamo in un piano con funzioni, stack e stima.',
    price: 300,
    priceLabel: '300 €',
    time: '3–5 giorni',
    accent: '#a78bfa', accent2: '#c4b5fd',
    seo: {
      title: 'Consulenza tecnica e analisi progetto app',
      description: 'Analisi di un progetto di app, SaaS o web app: funzioni, stack, architettura e stima di costi e tempi. Da 300 € + IVA, scalati dal preventivo se si procede.',
    },
    hero: {
      titleLead: 'Prima di scrivere codice,',
      titleAccent: 'un piano chiaro.',
      intro: 'Partiamo dalla tua idea e arriviamo a un documento concreto: cosa costruire per primo, con quale tecnologia, quanto costa e in quanto tempo. Se poi sviluppiamo insieme, il costo dell\'analisi viene scalato dal preventivo.',
    },
    about: {
      eyebrow: '// per chi',
      title: 'Per chi ha un\'idea, o un progetto bloccato.',
      text: [
        'Startup e aziende che vogliono un\'app o una piattaforma ma non sanno da dove partire, oppure hanno già un preventivo e vogliono un secondo parere tecnico.',
        'Anche per chi ha già un prodotto: revisione dell\'architettura e del codice, colli di bottiglia, scelta tra riscrivere o migliorare.',
      ],
    },
    items: [
      { icon: '🎯', title: 'Analisi dell\'idea', desc: 'Chi la userà, quale problema risolve, quali funzioni servono subito e quali possono aspettare.' },
      { icon: '🧭', title: 'Scelta dello stack', desc: 'App nativa o cross-platform, database, hosting, servizi esterni: con i costi mensili che comportano.' },
      { icon: '🏗️', title: 'Architettura', desc: 'Struttura dei dati, ruoli e permessi, integrazioni. Il punto dove si evitano i rifacimenti futuri.' },
      { icon: '📄', title: 'Documento finale', desc: 'Funzioni dell\'MVP, roadmap, stima di costi e tempi. È tuo: puoi usarlo anche con altri sviluppatori.' },
      { icon: '🔍', title: 'Code review (se c\'è già codice)', desc: 'Revisione del progetto esistente con le priorità di intervento.' },
    ],
    stack: ['Analisi', 'Architettura', 'Code review', 'Roadmap', 'Stima costi'],
    related: { to: '/progetti', label: 'Guarda i progetti realizzati', footerLabel: 'Progetti' },
    faq: [
      { q: 'Il costo dell\'analisi si recupera?', a: 'Sì: se dopo l\'analisi sviluppiamo il progetto insieme, i 300 € vengono scalati dal preventivo.' },
      { q: 'Cosa ricevo alla fine?', a: 'Un documento con le funzioni dell\'MVP, lo stack consigliato, l\'architettura e la stima di costi e tempi. Resta tuo.' },
    ],
  },
  {
    slug: 'sviluppo-siti-web',
    image: { height: 667, alt: 'Sito web e dashboard su pannelli di vetro luminosi' },
    name: 'Sito e web app',
    short: 'Siti su misura veloci e indicizzabili, o web app con area riservata, database e pagamenti.',
    price: 1500,
    priceLabel: '1.500 €',
    time: '2–4 settimane',
    accent: '#22d3ee', accent2: '#67e8f9',
    seo: {
      title: 'Sviluppo siti web e web app su misura',
      description: 'Siti web su misura in React e Next.js, veloci e ottimizzati per Google, e web app con area riservata, ruoli e database. Sito da 1.500 €, web app da 4.000 € + IVA.',
    },
    hero: {
      titleLead: 'Siti e web app',
      titleAccent: 'scritti su misura.',
      intro: 'Niente temi preconfezionati: un sito veloce, ottimizzato per Google e facile da far crescere. Quando serve di più, una web app con area riservata, ruoli, database e pagamenti.',
    },
    about: {
      eyebrow: '// per chi',
      title: 'Professionisti, attività e aziende.',
      text: [
        'Sito da 1.500 € + IVA: presentazione dell\'attività, servizi, contatti, SEO tecnica e analytics.',
        'Web app da 4.000 € + IVA: piattaforme con login, ruoli, prenotazioni, pagamenti o pannello di gestione. Un esempio è la piattaforma di Accademia Campania, con area riservata a tre ruoli.',
      ],
    },
    items: [
      { icon: '🎨', title: 'Design su misura', desc: 'Interfaccia disegnata per la tua attività, responsive su telefono, tablet e desktop.' },
      { icon: '🔎', title: 'SEO tecnica', desc: 'Titoli, descrizioni, dati strutturati, sitemap e pagine leggibili da Google già al primo caricamento.' },
      { icon: '⚡', title: 'Prestazioni', desc: 'Immagini ottimizzate, caricamenti rapidi, punteggi PageSpeed verificati prima della consegna.' },
      { icon: '🔐', title: 'Area riservata (web app)', desc: 'Login, ruoli e permessi, con le regole di accesso applicate dal database.' },
      { icon: '📈', title: 'Analytics e form', desc: 'Google Analytics 4 e Search Console collegati, form di contatto tracciati.' },
      { icon: '🚀', title: 'Messa online', desc: 'Dominio, hosting e deploy configurati sui tuoi account.' },
    ],
    stack: ['React', 'Next.js', 'TypeScript', 'Supabase', 'Firebase', 'Vercel', 'GA4'],
    related: { to: '/progetti/accademia-campania', label: 'Guarda il caso Accademia Campania', footerLabel: 'Accademia Campania' },
    priceNote: 'sito · web app da 4.000 €',
    faq: [
      { q: 'Che differenza c\'è tra sito e web app?', a: 'Il sito presenta l\'attività. La web app fa lavorare gli utenti: login, dati, prenotazioni, pagamenti. Il prezzo di partenza è 1.500 € per il sito e 4.000 € per la web app, IVA esclusa.' },
      { q: 'Usi WordPress?', a: 'No: siti e web app sono scritti su misura in React o Next.js. Sono più veloci e non dipendono da plugin di terzi.' },
    ],
  },
  {
    slug: 'sviluppo-app-mobile',
    image: { height: 667, alt: 'Due smartphone con interfacce di app' },
    name: 'MVP app mobile iOS e Android',
    short: 'La prima versione della tua app, per iOS e Android, fino alla pubblicazione sugli store.',
    price: 5000,
    priceLabel: '5.000 €',
    time: '3–6 settimane',
    accent: '#7c3aed', accent2: '#a78bfa',
    seo: {
      title: 'Sviluppo app mobile iOS e Android — MVP',
      description: 'Sviluppo di app mobile iOS e Android in React Native: login, backend, notifiche e pubblicazione su App Store e Google Play. MVP da 5.000 € + IVA in 3–6 settimane.',
    },
    hero: {
      titleLead: 'La tua app,',
      titleAccent: 'su App Store e Google Play.',
      intro: 'Un solo codice per iOS e Android con React Native. Partiamo dalle funzioni che servono davvero, pubblichiamo, e si cresce in base a come la usano le persone.',
    },
    about: {
      eyebrow: '// per chi',
      title: 'Startup, attività, professionisti con un\'idea di app.',
      text: [
        'Con gli strumenti di AI nello sviluppo i tempi si accorciano, e il risparmio va nel prezzo: un MVP parte da 5.000 € + IVA.',
        'Ho pubblicato app sugli store come iCoach, per il coaching fitness, e sviluppato Veyro e Veyro Trainer, app per palestre e personal trainer.',
      ],
    },
    items: [
      { icon: '📱', title: 'iOS e Android', desc: 'Un solo codice in React Native con Expo: stesse funzioni e stesso aspetto sui due sistemi.' },
      { icon: '👤', title: 'Login e profilo', desc: 'Registrazione, accesso, profilo utente e recupero password.' },
      { icon: '🗄️', title: 'Backend e database', desc: 'Firebase o Supabase, con regole di accesso ai dati e funzioni server.' },
      { icon: '🔔', title: 'Notifiche push', desc: 'Notifiche per richiamare gli utenti nei momenti giusti.' },
      { icon: '🏪', title: 'Pubblicazione sugli store', desc: 'Schede, screenshot, privacy policy e invio in revisione su App Store e Google Play.' },
      { icon: '📊', title: 'Analytics', desc: 'Eventi tracciati per capire come viene usata l\'app e cosa migliorare.' },
    ],
    stack: ['React Native', 'Expo', 'TypeScript', 'Firebase', 'Supabase', 'Stripe'],
    related: { to: '/icoach', label: 'Guarda iCoach, app pubblicata', footerLabel: 'iCoach' },
    faq: [
      { q: 'I tempi includono la revisione degli store?', a: 'No: Apple e Google impiegano di solito qualche giorno per approvare l\'app, e i loro tempi non dipendono da me. Le 3–6 settimane sono lo sviluppo.' },
      { q: 'Gli account sviluppatore sono compresi?', a: 'No: l\'account Apple Developer e quello Google Play sono intestati a te e li paghi direttamente ad Apple e Google. Ti guido nella creazione.' },
    ],
  },
  {
    slug: 'sviluppo-saas-gestionali',
    image: { height: 667, alt: 'Dashboard di un gestionale con grafici' },
    name: 'SaaS e gestionali su misura',
    short: 'Piattaforme con ruoli, dashboard e pagamenti ricorrenti, costruite sui tuoi processi.',
    price: 4000,
    priceLabel: '4.000 €',
    time: '3–6 settimane',
    accent: '#f472b6', accent2: '#f9a8d4',
    seo: {
      title: 'Sviluppo SaaS e gestionali su misura',
      description: 'Sviluppo di SaaS e gestionali su misura per aziende e startup: ruoli e permessi, dashboard, pagamenti ricorrenti con Stripe, API. Da 4.000 € + IVA in 3–6 settimane.',
    },
    hero: {
      titleLead: 'Un gestionale',
      titleAccent: 'che segue il tuo lavoro.',
      intro: 'Al posto di fogli Excel e software generici, una piattaforma costruita sui tuoi processi: ruoli, dashboard, pagamenti ricorrenti. Oppure il tuo SaaS da vendere in abbonamento.',
    },
    about: {
      eyebrow: '// per chi',
      title: 'Aziende con processi da digitalizzare, startup SaaS.',
      text: [
        'Si parte da un primo nucleo di funzioni, da 4.000 € + IVA, e si aggiunge il resto per fasi. Gli strumenti di AI accorciano lo sviluppo e il risparmio va nel prezzo.',
        'Esempi: CrewBoat, per la gestione degli equipaggi nautici, e Veyro Trainer, app B2B per personal trainer con gestione clienti e pagamenti Stripe Connect.',
      ],
    },
    items: [
      { icon: '🏢', title: 'Multi-azienda', desc: 'Ogni cliente vede solo i propri dati, con separazione garantita dal database.' },
      { icon: '🔑', title: 'Ruoli e permessi', desc: 'Admin, operatori, clienti: ognuno vede e fa solo quello che deve.' },
      { icon: '📊', title: 'Dashboard', desc: 'I numeri che ti servono per decidere, aggiornati in tempo reale.' },
      { icon: '💳', title: 'Pagamenti ricorrenti', desc: 'Abbonamenti, fatture e rinnovi con Stripe.' },
      { icon: '🔌', title: 'API e integrazioni', desc: 'Collegamento con i software che usi già, email transazionali, esportazioni.' },
      { icon: '🧪', title: 'Staging e test', desc: 'Ambiente di prova separato dalla produzione, per rilasciare senza sorprese.' },
    ],
    stack: ['React', 'Next.js', 'Node.js', 'TypeScript', 'Supabase', 'Stripe', 'Vercel'],
    related: { to: '/crewboat', label: 'Guarda CrewBoat', footerLabel: 'CrewBoat' },
    faq: [
      { q: 'Cosa comprende il prezzo di partenza?', a: 'Un primo nucleo: login, ruoli, le funzioni principali del tuo processo e una dashboard. Le altre funzioni si stimano e si aggiungono per fasi.' },
      { q: 'Posso venderlo in abbonamento ad altre aziende?', a: 'Sì: la piattaforma può nascere multi-azienda, con pagamenti ricorrenti via Stripe.' },
    ],
  },
  {
    slug: 'manutenzione-app',
    image: { height: 1000, alt: 'Smartphone sincronizzato con un server, con scudo di sicurezza' },
    name: 'Manutenzione e assistenza',
    short: 'Un pacchetto di ore al mese per aggiornamenti, correzioni e nuove funzioni.',
    price: 50,
    priceUnit: 'ora',
    priceLabel: '50 €/h',
    time: 'Mensile',
    accent: '#34d399', accent2: '#6ee7b7',
    seo: {
      title: 'Manutenzione app e siti — pacchetti ore',
      description: 'Manutenzione di app, siti e web app a pacchetti di ore mensili: aggiornamenti, correzioni, nuove funzioni. 50 €/h + IVA, pacchetti da 5, 10 o 20 ore.',
    },
    hero: {
      titleLead: 'La tua app,',
      titleAccent: 'sempre aggiornata.',
      intro: 'Sistemi operativi, store e librerie cambiano di continuo. Con un pacchetto di ore al mese la tua app o il tuo sito restano aggiornati, e c\'è sempre qualcuno che conosce il codice.',
    },
    about: {
      eyebrow: '// pacchetti ore',
      title: '5, 10 o 20 ore al mese.',
      text: [
        '5 ore: 250 € + IVA al mese. 10 ore: 500 € + IVA al mese. 20 ore: 1.000 € + IVA al mese. Tariffa di 50 €/h.',
        'Vale per i progetti sviluppati da me e anche per app e siti esistenti, dopo una prima analisi del codice.',
      ],
    },
    items: [
      { icon: '🔄', title: 'Aggiornamenti', desc: 'Nuove versioni di iOS, Android, librerie e requisiti degli store.' },
      { icon: '🐛', title: 'Correzioni', desc: 'Bug segnalati dagli utenti o emersi dai report di errore.' },
      { icon: '✨', title: 'Piccole funzioni', desc: 'Miglioramenti e nuove funzioni che stanno nel pacchetto di ore.' },
      { icon: '🛡️', title: 'Sicurezza e backup', desc: 'Controllo di dipendenze, regole di accesso e backup dei dati.' },
      { icon: '📬', title: 'Report mensile', desc: 'Cosa è stato fatto e quante ore sono state usate.' },
    ],
    stack: ['React Native', 'React', 'Next.js', 'Firebase', 'Supabase', 'Expo EAS'],
    related: { to: '/servizi', label: 'Tutti i servizi', footerLabel: 'Servizi' },
    faq: [
      { q: 'Posso affidarti la manutenzione di un\'app non sviluppata da te?', a: 'Sì, dopo una prima analisi del codice per capire stato e priorità.' },
    ],
  },
]

/* ─── Pagina del singolo pacchetto: dati → ProductPage ──────── */
export function ServizioPage({ slug }) {
  const s = SERVIZI.find(x => x.slug === slug)
  const to = `/collabora?servizio=${s.slug}`
  return (
    <ProductPage
      data={{
        slug: s.slug,
        accent: s.accent,
        accent2: s.accent2,
        seo: { ...s.seo, canonical: `/servizi/${s.slug}`, ogImage: `${SITE}/images/servizi/og-${s.slug}.jpg`, jsonLd: serviceJsonLd(s) },
        eyebrow: '// servizi',
        badge: `Da ${s.priceLabel} + IVA`,
        hero: {
          ...s.hero, ctaTo: to, ctaLabel: 'Chiedi un preventivo',
          image: `/images/servizi/${s.slug}.webp`, imageWidth: 1000, imageHeight: s.image.height,
          imageAlt: s.image.alt, imageMaxWidth: s.image.height === 1000 ? '420px' : '560px',
        },
        related: s.related,
        stats: [
          { icon: '💶', label: s.priceNote ? `a partire da, IVA esclusa · ${s.priceNote}` : 'a partire da, IVA esclusa', value: s.priceLabel, color: s.accent },
          { icon: '⏱️', label: 'tempi tipici', value: s.time, color: '#22d3ee' },
          { icon: '📍', label: 'con trasferte quando serve', value: 'Remoto', color: '#f472b6' },
        ],
        about: s.about,
        features: { eyebrow: '// cosa include', title: 'Cosa è incluso.', lead: s.short, items: s.items },
        stack: s.stack,
        faq: [...s.faq, ...FAQ_COMUNI],
        cta: {
          eyebrow: '// preventivo',
          titleLead: 'Raccontami',
          titleAccent: 'il tuo progetto.',
          text: 'Anche un\'idea grezza va bene. Rispondo con domande precise e, quando è chiaro cosa serve, un preventivo scritto.',
          to,
          label: 'Chiedi un preventivo',
        },
      }}
    />
  )
}

/* ─── Pagina indice /servizi ─────────────────────────────────── */
function Reveal({ children, delay = 0 }) {
  const ref = useRef(null)
  const inView = useInView(ref, { once: true, margin: '-60px' })
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 28 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1], delay }}
      style={{ height: '100%' }}
    >
      {children}
    </motion.div>
  )
}

const eyebrow = {
  fontFamily: 'var(--font-mono)', fontSize: '0.82rem', color: 'var(--color-accent-mid)',
  letterSpacing: '0.12em', textTransform: 'uppercase', display: 'block', marginBottom: '1rem',
}

export default function ServiziPage() {
  useEffect(() => { window.scrollTo(0, 0) }, [])

  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'OfferCatalog',
        '@id': `${SITE}/servizi#catalog`,
        name: 'Servizi VMDProject',
        url: `${SITE}/servizi`,
        provider: { '@id': `${SITE}/#org` },
        itemListElement: SERVIZI.map(s => ({ '@id': `${SITE}/servizi/${s.slug}#service` })),
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE}/` },
          { '@type': 'ListItem', position: 2, name: 'Servizi', item: `${SITE}/servizi` },
        ],
      },
    ],
  }

  return (
    <>
      <PageSEO
        title="Servizi e prezzi — app, SaaS e siti web"
        description="Pacchetti di VMDProject con prezzi di partenza: consulenza da 300 €, siti da 1.500 €, MVP app iOS e Android da 5.000 €, SaaS da 4.000 €, manutenzione a 50 €/h. IVA esclusa."
        canonical="/servizi"
        ogImage={`${SITE}/images/servizi/og-servizi.jpg`}
        jsonLd={jsonLd}
      />
      <Navbar />
      <motion.main
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.4 }}
        style={{ background: 'var(--color-bg)', minHeight: '100vh' }}
      >
        <section style={{ paddingTop: '9rem', paddingBottom: '3rem' }}>
          <div className="container">
            <span style={eyebrow}>{"// servizi e prezzi"}</span>
            <h1 style={{
              fontFamily: 'var(--font-display)', fontWeight: 800,
              fontSize: 'clamp(2.2rem, 5.5vw, 3.8rem)', lineHeight: 1.1,
              letterSpacing: '-0.03em', marginBottom: '1.25rem', maxWidth: '20ch',
            }}>
              Cosa costruisco,{' '}
              <span className="gradient-text">quanto costa.</span>
            </h1>
            <p style={{ color: 'var(--color-text-2)', fontSize: '1.1rem', lineHeight: 1.8, maxWidth: '60ch', fontWeight: 300 }}>
              Prezzi di partenza chiari, IVA esclusa. Con gli strumenti di AI nello sviluppo i tempi si
              sono accorciati, e il risparmio va nel prezzo. Il costo finale arriva con un preventivo
              scritto, dopo aver capito cosa ti serve.
            </p>
            <img
              src="/images/servizi/servizi.webp"
              alt="Icone dei cinque servizi: progetto, sito web, app, dashboard e scudo"
              width={1200} height={330}
              style={{ width: '100%', height: 'auto', display: 'block', marginTop: '2rem', borderRadius: 'var(--radius-lg)' }}
            />
          </div>
        </section>

        <section style={{ paddingBottom: '4rem' }}>
          <div className="container">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(300px, 100%), 1fr))', gap: '1.5rem' }}>
              {SERVIZI.map((s, i) => (
                <Reveal key={s.slug} delay={i * 0.06}>
                  <Link
                    to={`/servizi/${s.slug}`}
                    style={{
                      display: 'flex', flexDirection: 'column', gap: '1rem', height: '100%',
                      background: 'rgba(255,255,255,0.025)', border: `1px solid ${s.accent}30`,
                      borderTop: `2px solid ${s.accent}`, borderRadius: 'var(--radius-lg)',
                      padding: '1.75rem', textDecoration: 'none', color: 'var(--color-text)',
                      transition: 'transform 0.3s, border-color 0.3s',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.borderColor = `${s.accent}70` }}
                    onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.borderColor = `${s.accent}30` }}
                  >
                    <h2 style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: '1.2rem', margin: 0 }}>{s.name}</h2>
                    <p style={{ color: 'var(--color-text-2)', fontSize: '0.98rem', lineHeight: 1.65, margin: 0, flex: 1 }}>{s.short}</p>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '1rem', flexWrap: 'wrap' }}>
                      <span style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: '1.5rem', color: s.accent2 }}>
                        <span style={{ fontSize: '0.8rem', fontWeight: 400, color: 'var(--color-muted)' }}>da </span>
                        {s.priceLabel}
                      </span>
                      <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.78rem', color: 'var(--color-muted)' }}>{s.time}</span>
                    </div>
                    <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.8rem', color: s.accent2 }}>Dettagli →</span>
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        <section style={{ paddingTop: '2rem', paddingBottom: '5rem' }}>
          <div className="container">
            <span style={eyebrow}>{"// condizioni"}</span>
            <h2 style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 'clamp(1.7rem, 3.5vw, 2.4rem)', letterSpacing: '-0.02em', marginBottom: '2rem' }}>
              Valgono per tutti i pacchetti.
            </h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(240px, 100%), 1fr))', gap: '1.25rem' }}>
              {CONDIZIONI.map(c => (
                <div key={c.title} className="glass-card" style={{ padding: '1.5rem', borderRadius: 'var(--radius-lg)' }}>
                  <div style={{ fontSize: '1.5rem', marginBottom: '0.6rem' }}>{c.icon}</div>
                  <h3 style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: '1.02rem', marginBottom: '0.5rem' }}>{c.title}</h3>
                  <p style={{ color: 'var(--color-text-2)', fontSize: '0.92rem', lineHeight: 1.65, margin: 0 }}>{c.desc}</p>
                </div>
              ))}
            </div>

            <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginTop: '3rem' }}>
              <Link to="/collabora" className="btn-primary">Chiedi un preventivo</Link>
              <Link to="/progetti" className="btn-secondary">Guarda i progetti</Link>
            </div>
          </div>
        </section>
      </motion.main>
    </>
  )
}
